import { CarouselItemId } from '@homex/se-widget-flow-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  addMediaId,
  removeMedia,
  removeMediaId,
  removeUploadedMediaFileRecord,
  setCurrentCarouselItem,
  setMediaUploadSelection,
} from 'actions';
import { ReactComponent as AddIconSvg } from 'components/svg/AddIcon.svg';
import { ReactComponent as CloseSvg } from 'components/svg/Close.svg';
import { ReactComponent as TrashcanSvg } from 'components/svg/Trashcan.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  IBrowserImage,
  ICarouselItemMediaUploadBoxProps,
  IState,
} from 'typings';

import {
  CancelButton,
  DeleteButton,
  FileBox,
  ImagePreview,
  ProgressBarInner,
  ProgressBarOuter,
  Tooltip,
  TooltipBox,
  UploadBox,
  VideoPreview,
} from './styles';

export const CarouselItemMediaUploadBox = ({
  file,
  mediaIdIndex,
  onCancel: handleCancel,
  onNoFileClick: handleNoFileClick,
}: ICarouselItemMediaUploadBoxProps) => {
  const dispatch = useThunkDispatch();
  const { mediaIds, sessionToken } = useSelector((state: IState) => ({
    mediaIds: state.userInput.mediaIds,
    sessionToken: state.auth.sessionToken,
  }));

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [xhr, setXhr] = useState<XMLHttpRequest | undefined>();
  const fileRef = useRef(file);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleUploadRemove = async () => {
    revokeImagePreview();

    try {
      const mediaId = mediaIds[mediaIdIndex];

      if (mediaId) {
        dispatch(removeMediaId(mediaId));
        await dispatch(removeMedia(mediaId));
        dispatch(removeUploadedMediaFileRecord(mediaIdIndex));
      }
    } catch {
      // TODO: SE-484
    }
  };

  const handleUploadCancel = () => {
    handleCancel();

    if (xhr) {
      xhr.abort();
    }

    revokeImagePreview();
    setProgress(0);
    setXhr(undefined);
  };

  // https://github.com/react-dropzone/react-dropzone#word-of-caution-when-working-with-previews
  const revokeImagePreview = () => {
    if (imagePreview) {
      window.URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const sendRequest = useCallback(
    (mediaFile: File) => {
      const mediaXhr = new XMLHttpRequest();
      const uri = process.env.REACT_APP_MEDIA_URI;
      mediaXhr.open('POST', uri, true);
      mediaXhr.responseType = 'json';

      if (sessionToken) {
        mediaXhr.setRequestHeader('Authorization', `Bearer ${sessionToken}`);
      }

      mediaXhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          setProgress((event.loaded / event.total) * 100);
        }
      });

      mediaXhr.onreadystatechange = () => {
        if (mediaXhr.readyState === 4) {
          const response =
            typeof mediaXhr.response === 'string'
              ? JSON.parse(mediaXhr.response)
              : mediaXhr.response;

          if (mediaXhr.status !== 0 && response.id) {
            dispatch(addMediaId(response.id, mediaIdIndex));
          }

          setProgress(0);
          setXhr(undefined);
        }
      };

      const formData = new FormData();
      // Server requires append's 'name' param to be 'file'
      formData.append('file', mediaFile);
      mediaXhr.send(formData);
      setXhr(mediaXhr);
    },
    [dispatch, mediaIdIndex, sessionToken],
  );

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const targetCoords = event.currentTarget.getBoundingClientRect();
    const currentTooltipRef = tooltipRef.current;

    if (currentTooltipRef) {
      currentTooltipRef.style.left = `${
        event.pageX - targetCoords.left + 13
      }px`;
      currentTooltipRef.style.top = `${
        event.pageY - window.scrollY - targetCoords.top + 13
      }px`;
    }
  };

  const isVideo = !!(file && /video/.test(file.type));
  const isLoading = progress !== 0 && progress !== 100;

  const getPreview = () => {
    if (!file) {
      return null;
    }

    return isVideo ? (
      <VideoPreview isLoading={isLoading} />
    ) : (
      <ImagePreview alt="" isLoading={isLoading} src={imagePreview || ''} />
    );
  };

  const handleDetailClick = (selection?: IBrowserImage) => {
    dispatch(setMediaUploadSelection(selection));
    dispatch(
      setCurrentCarouselItem(
        CarouselReduxReducer.SERVICE,
        CarouselItemId.MediaDetail,
      ),
    );
  };

  useEffect(() => {
    if (file) {
      setImagePreview(window.URL.createObjectURL(file));
    }

    if (file && fileRef.current !== file) {
      if (!mediaIds[mediaIdIndex]) {
        sendRequest(file);
      }

      fileRef.current = file;
    }
  }, [file, mediaIdIndex, mediaIds, sendRequest]);

  const innerStyleWidth = { width: `${progress}%` };

  let browserImage: IBrowserImage | undefined;

  if (file) {
    browserImage = {
      name: file.name,
      preview: imagePreview || '',
      type: file.type,
    };
  }

  if (file && isLoading) {
    return (
      <TooltipBox onMouseMove={handleMouseMove}>
        <CancelButton icon={<CloseSvg />} onClick={handleUploadCancel} />
        <FileBox onClick={handleDetailClick.bind(this, browserImage)}>
          {getPreview()}
          <ProgressBarOuter>
            <ProgressBarInner style={innerStyleWidth} />
          </ProgressBarOuter>
        </FileBox>
        <Tooltip ref={tooltipRef}>{file.name}</Tooltip>
      </TooltipBox>
    );
  } else if (file) {
    return (
      <TooltipBox onMouseMove={handleMouseMove}>
        <DeleteButton icon={<TrashcanSvg />} onClick={handleUploadRemove} />
        <FileBox onClick={handleDetailClick.bind(this, browserImage)}>
          {getPreview()}
        </FileBox>
        <Tooltip ref={tooltipRef}>{file.name}</Tooltip>
      </TooltipBox>
    );
  }

  return (
    <UploadBox onClick={handleNoFileClick}>
      <AddIconSvg />
    </UploadBox>
  );
};
